import Button from "components/button";
import { Checkbox } from "components/check-box/Checkbox";
import Loading from "components/loading";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "store/hooks";
import type users from "../users";

import styles from "./edit-permissions.module.scss";

interface UserPermissionObject {
	title: string;
	checked: boolean;
	value: string;
}

const EditUserPermissionsModal = ({
	onClose,
	selectedUser,
}: {
	selectedUser?: users.user;
	onClose: () => void;
}) => {
	const dispatch = useDispatch();

	const { pending, data } = useSelector((state) => state.permissions);
	const { pending: userPermissionsPending, data: userPermissionsData } =
		useSelector((state) => state.userPermissions);
	const user = useSelector((state) => state.user);

	const [userPermissions, setUserPermissions] = useState<
		UserPermissionObject[]
	>([]);

	const [allAvailablePermissions, setAllAvailablePermissions] = useState<
		(string | { name?: string; title?: string; value?: string })[]
	>([]);

	useEffect(() => {
		if (selectedUser) {
			dispatch.permissions.getAllPermissions();
			dispatch.userPermissions.execute({ username: selectedUser.email });
		}
	}, [dispatch, selectedUser]);

	useEffect(() => {
		if (!data) return;
		if (Array.isArray(data)) {
			setAllAvailablePermissions(data);
		}
	}, [data]);

	useEffect(() => {
		if (!allAvailablePermissions.length || !selectedUser) return;

		const permissionsWithStatus = allAvailablePermissions.map((permission) => {
			const permissionValue =
				typeof permission === "string"
					? permission
					: permission.value || permission.name || permission.title || "";

			const permissionTitle =
				typeof permission === "string"
					? permission
					: permission.name || permission.title || permission.value || "";

			const userHasPermission =
				userPermissionsData?.includes(permissionValue) || false;

			return {
				title: permissionTitle,
				value: permissionValue,
				checked: userHasPermission,
			};
		});

		setUserPermissions(permissionsWithStatus);
	}, [allAvailablePermissions, selectedUser, userPermissionsData]);

	return (
		<div className={styles.modal} onMouseDown={onClose}>
			<form
				className={styles.modalWrap}
				onMouseDown={(event) => event.stopPropagation()}
			>
				<div className={styles.title}>
					User rights {selectedUser?.email}
					{user && (
						<div className={styles.roleInfo}>Role: {selectedUser?.role}</div>
					)}
				</div>
				{pending || userPermissionsPending ? (
					<div className={styles.loadingContainer}>
						<Loading />
					</div>
				) : (
					<div className={styles.permissionsContainer}>
						{userPermissions?.map((item, index) => (
							<Checkbox
								key={item.value}
								checked={item.checked}
								title={item.title}
								disabled={selectedUser?.role !== "custom"}
								onChange={() => {
									if (item.checked) {
										dispatch.userPermissions.deletePermission({
											username: selectedUser?.email as string,
											permission: item.value,
										});
									} else {
										dispatch.userPermissions.editPermission({
											username: selectedUser?.email as string,
											permission: item.value,
										});
									}
								}}
							/>
						))}
					</div>
				)}

				<div className={styles.buttonGroup}>
					<Button color="orange" onClick={onClose}>
						Cancel
					</Button>
				</div>
			</form>
		</div>
	);
};

export default EditUserPermissionsModal;
